import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isReadOnlyModule } from './read-only-flags';
import { resolveModuleIdForUrl } from './module-write-scopes';

/**
 * Stops a Viewer's writes before any request is sent.
 *
 * A user with the Viewer role in a module can see everything a User sees but must not change
 * anything. This is a client-side guarantee only — see the class comment for what that does and
 * doesn't cover — but it is the thing that actually stops a click on Save from doing anything:
 * `next.handle()` is never called, so no network request happens at all.
 *
 * Two decisions here exist specifically because an earlier version of this got them wrong and it
 * cost a very bad evening:
 *
 *  - **Only PUT, PATCH and DELETE are blocked. POST is never touched.** This codebase uses POST for
 *    reads constantly — `dms/search`, `meeting-notes/query`, the compliance/risk tables,
 *    `timesheets/reports/per-customer`, `leave/validate` all take a filter/body and have to be
 *    POSTs. A URL-keyword guess at "is this POST actually a write?" is exactly the kind of thing
 *    that goes wrong on one page nobody tested, and when it does, the blocked call errors and
 *    whatever was awaiting it never resolves — the page hangs on its own loading spinner forever.
 *    That is a broken app, not a restriction. PUT/PATCH/DELETE are unambiguous in this codebase, so
 *    blocking exactly those three verbs can never break a read.
 *  - **No service injection.** This class depends on nothing but MatSnackBar. The read-only check is
 *    a plain function reading sessionStorage directly (see read-only-flags.ts) rather than going
 *    through a service that resolves ModulesService → TenantContextService → HttpClient. An
 *    HttpInterceptor is constructed on the first outgoing request; injecting a service with its own
 *    HttpClient dependency there is fragile at best.
 *
 * What this does NOT do: disable the button, or stop a devtools/Postman request with someone else's
 * token. Visual disabling is ReadOnlyUiGuard's job; there is currently no server-side enforcement
 * (a deliberate scope decision — see project memory on this feature for why).
 */
const BLOCKED_METHODS = new Set(['PUT', 'PATCH', 'DELETE']);

@Injectable()
export class ReadOnlyInterceptor implements HttpInterceptor {
  private readonly snack = inject(MatSnackBar);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.shouldBlock(request)) return next.handle(request);

    this.snack.open(
      'You have read-only access to this module, so this change was not saved.',
      'Close',
      { duration: 5000 }
    );

    return throwError(() => new HttpErrorResponse({
      status: 403,
      statusText: 'Forbidden',
      url: request.url,
      error: { errors: 'Read-only access: you cannot make changes in this module.' },
    }));
  }

  private shouldBlock(request: HttpRequest<unknown>): boolean {
    if (!BLOCKED_METHODS.has((request.method || '').toUpperCase())) return false;

    const moduleId = resolveModuleIdForUrl(request.url);
    if (moduleId === null) return false;

    return isReadOnlyModule(moduleId);
  }
}
